import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/button';
import ButtonSpinner from '@/components/button-spinner';
import { wallet as IconWallet } from '@/components/icons/wallet';
import { useDucatClient } from '@/context/DucatClientCtx';
import { WalletType, type WalletTypeExtended } from '@/context/types';
import { usePreventMultipleClicks } from '@/hooks/usePreventMultipleClicks';
import { connectMetaMask } from '@/lib/connect-metamask';
import { isFlaskConnectionAvailable, type MetaMaskConnectionMode } from '@/lib/metamask-connection-mode';
import { connectionPurposeForPath, type WalletConnectionPurpose, walletsForPurpose } from '@/lib/wallet-policy';
import { useAppStore } from '@/stores/useAppStore';
import { cn } from '@/utils/cn';

const WalletConnectionModal = dynamic(() => import('./WalletConnectionModal'));

interface WalletConnectButtonProps {
  purpose?: WalletConnectionPurpose;
  label?: string;
  mobileStylesOn: boolean;
  isSmall?: boolean;
}

const WalletConnectButton: React.FC<WalletConnectButtonProps> = ({
  mobileStylesOn,
  isSmall = false,
  purpose,
  label,
}: WalletConnectButtonProps) => {
  const t = useTranslations('layout.wallet');
  const pathname = usePathname();
  const connectionPurpose = purpose ?? connectionPurposeForPath(pathname ?? '/');
  const { client, contract, isContractLoading, contractError, retryContract } = useDucatClient();
  const needsRetry = !contract && !!contractError;
  const isPreparing = !contract && isContractLoading;
  const setWalletErrorMessage = useAppStore((s) => s.setWalletErrorMessage);
  const walletErrorMessage = useAppStore((s) => s.walletErrorMessage);

  const connectionPending = useRef(false);
  const connectButtonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [showFlask, setShowFlask] = useState(false);
  useEffect(() => setShowFlask(isFlaskConnectionAvailable()), []);

  // use custom hook for preventing multiple clicks on wallet connection
  const { isButtonBlocked, blockButton, unblockButton } = usePreventMultipleClicks();

  const handleConnect = async (
    walletType: WalletType | WalletTypeExtended = WalletType.Metamask,
    mode: MetaMaskConnectionMode = 'default',
  ) => {
    // prevent multiple clicks
    if (connectionPending.current || !walletsForPurpose(connectionPurpose).includes(walletType as WalletType)) {
      return;
    }

    connectionPending.current = true;
    setWalletErrorMessage('');
    blockButton();
    try {
      if (needsRetry) {
        await retryContract();
        return; // Connecting requires a fresh click after the configuration loads.
      }
      const connected =
        walletType === WalletType.Metamask
          ? await connectMetaMask((type) => client.connect(type), mode)
          : await client.connect(walletType as WalletType);
      if (connected) setOpen(false);
      else setOpen(true);
    } catch {
    } finally {
      connectionPending.current = false;
      unblockButton();
    }
  };

  return (
    <div className="inline-flex flex-col items-center gap-1">
      <Button
        ref={connectButtonRef}
        data-testid="vault-connect-wallet"
        leadingIcon={
          isButtonBlocked || isPreparing ? (
            <ButtonSpinner isSmall={isSmall} />
          ) : (
            <IconWallet
              className={cn(
                mobileStylesOn ? 'h-[18px] w-[18px] lg:h-[24px] lg:w-[24px]' : 'h-[24px] w-[24px]',
                isSmall && 'h-[18px] w-[18px]',
              )}
            />
          )
        }
        variant="secondary"
        className={cn(
          'focus-visible:ring-1 focus-visible:ring-border-secondary',
          mobileStylesOn
            ? 'text-sm lg:text-base py-1.5 lg:py-2 px-2 lg:px-4 gap-1 lg:gap-2 leading-4 lg:leading-6'
            : 'text-base py-2 px-4 gap-2 leading-6',
          isSmall && 'text-sm py-1.5 px-2 gap-1 leading-4',
        )}
        disabled={isButtonBlocked || isPreparing}
        aria-busy={isButtonBlocked || isPreparing}
        onClick={() => {
          setWalletErrorMessage('');
          if (showFlask && !needsRetry) setOpen(true);
          else void handleConnect();
        }}
      >
        <span className={cn(mobileStylesOn && !needsRetry && !isPreparing && 'sr-only lg:not-sr-only')}>
          {isPreparing ? t('loadingConnection') : needsRetry ? t('retryConnection') : (label ?? t('connect'))}
        </span>
      </Button>
      {needsRetry && !isPreparing && (
        <span role="status" className="max-w-64 text-xs text-text-secondary text-center">
          {t('connectionDataError')}
        </span>
      )}
      {!showFlask && walletErrorMessage && (
        <p role="alert" className="max-w-80 text-sm text-text-secondary">
          {walletErrorMessage}
        </p>
      )}
      {showFlask && open && (
        <WalletConnectionModal
          open={open}
          setOpen={setOpen}
          connectionPurpose={connectionPurpose}
          handleConnect={handleConnect}
          handleConnectFlask={() => handleConnect(WalletType.Metamask, 'flask')}
          isButtonBlocked={isButtonBlocked || isPreparing}
          errorMessage={walletErrorMessage}
          restoreFocus={() => connectButtonRef.current?.focus()}
        />
      )}
    </div>
  );
};

export default WalletConnectButton;
